import React, { useState, useEffect, useCallback } from 'react';
import { Music, Calendar, Mail, Phone, User, TrendingUp, Clock, CheckCircle, XCircle, AlertCircle, Lock, Eye, EyeOff, Star, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin password - Change this to your desired password
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'Lavalyeverafter23';

const statusColors = {
  pending: 'bg-yellow-500',
  contacted: 'bg-blue-500',
  confirmed: 'bg-green-500',
  declined: 'bg-red-500'
};

const statusIcons = {
  pending: AlertCircle,
  contacted: Clock,
  confirmed: CheckCircle,
  declined: XCircle
};

export const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [stats, setStats] = useState({
    total_bookings: 0,
    pending_bookings: 0,
    recent_bookings: []
  });
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if already authenticated (session storage)
  useEffect(() => {
    const authStatus = sessionStorage.getItem('admin_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    } else {
      setLoading(false);
    }
  }, []); // Empty deps - runs once on mount to check initial auth state

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsResponse, bookingsResponse, reviewsResponse] = await Promise.all([
        axios.get(`${API}/bookings/stats`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/admin/reviews`)
      ]);
      setStats(statsResponse.data);
      setBookings(bookingsResponse.data);
      setReviews(reviewsResponse.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_authenticated', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPasswordInput('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_authenticated');
    setPasswordInput('');
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const updateReviewStatus = async (reviewId, newStatus) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}/status?status=${newStatus}`);
      fetchData();
    } catch (err) {
      console.error('Error updating review status:', err);
      alert('Failed to update review status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Password Protection Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Card className="bg-[#1a1a1a] border-white/10 shadow-2xl">
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-4">
                <div className="bg-[#B8956A]/20 p-4 rounded-full">
                  <Lock className="h-12 w-12 text-[#B8956A]" />
                </div>
              </div>
              <CardTitle className="text-2xl text-white mb-2">Admin Dashboard</CardTitle>
              <p className="text-white/60 text-sm">J Lavaly Music - Protected Area</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                    Enter Admin Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordInput}
                      onChange={(e) => {
                        setPasswordInput(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="Enter password"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordError && (
                    <p className="text-red-500 text-sm mt-2">{passwordError}</p>
                  )}
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#B8956A] hover:bg-[#9a7a52] text-white transition-all duration-300"
                >
                  Access Dashboard
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="mt-6 text-center">
            <a href="/" className="text-[#B8956A] hover:text-white transition-colors text-sm">
              ← Back to Website
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-black border-b border-white/10 py-6 px-6">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Music className="h-8 w-8 text-[#B8956A]" />
              <div>
                <h1 className="text-2xl font-bold">J Lavaly Music</h1>
                <p className="text-white/60 text-sm">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-[#B8956A] hover:text-white transition-colors text-sm">
                Back to Website
              </a>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5 text-sm"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="py-8 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-white/80">Total Bookings</CardTitle>
                <TrendingUp className="h-4 w-4 text-[#B8956A]" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.total_bookings}</div>
                <p className="text-xs text-white/60 mt-1">All time inquiries</p>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-white/80">Pending</CardTitle>
                <AlertCircle className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.pending_bookings}</div>
                <p className="text-xs text-white/60 mt-1">Awaiting response</p>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-white/80">This Month</CardTitle>
                <Calendar className="h-4 w-4 text-[#B8956A]" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {bookings.filter(b => {
                    const bookingDate = new Date(b.created_at);
                    const now = new Date();
                    return bookingDate.getMonth() === now.getMonth() && 
                           bookingDate.getFullYear() === now.getFullYear();
                  }).length}
                </div>
                <p className="text-xs text-white/60 mt-1">Recent inquiries</p>
              </CardContent>
            </Card>
          </div>

          {/* Bookings Table */}
          <Card className="bg-[#1a1a1a] border-white/10">
            <CardHeader>
              <CardTitle className="text-xl text-white">All Booking Inquiries</CardTitle>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <div className="text-center py-12 text-white/60">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No booking inquiries yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => {
                    const StatusIcon = statusIcons[booking.status];
                    return (
                      <div key={booking.id} className="bg-black/30 p-6 rounded-lg border border-white/5 hover:border-[#B8956A]/30 transition-all">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-4 flex-1">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <User className="h-4 w-4 text-[#B8956A]" />
                                <h3 className="text-lg font-semibold text-white">{booking.name}</h3>
                              </div>
                              <div className="space-y-1 text-sm text-white/70">
                                <div className="flex items-center space-x-2">
                                  <Mail className="h-3 w-3" />
                                  <a href={`mailto:${booking.email}`} className="hover:text-[#B8956A] transition-colors">{booking.email}</a>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-3 w-3" />
                                  <a href={`tel:${booking.phone}`} className="hover:text-[#B8956A] transition-colors">{booking.phone}</a>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Calendar className="h-3 w-3" />
                                  <span>Event: {booking.event_type} on {booking.event_date}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${statusColors[booking.status]}/20`}>
                              <StatusIcon className={`h-4 w-4 ${statusColors[booking.status].replace('bg-', 'text-')}`} />
                              <span className="text-xs font-medium text-white capitalize">{booking.status}</span>
                            </div>
                            <select
                              value={booking.status}
                              onChange={(e) => updateStatus(booking.id, e.target.value)}
                              className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 py-1 focus:border-[#B8956A] focus:ring-[#B8956A]"
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="declined">Declined</option>
                            </select>
                          </div>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg">
                          <p className="text-sm text-white/80 leading-relaxed">{booking.message}</p>
                        </div>
                        <div className="mt-3 text-xs text-white/40">
                          Received: {formatDate(booking.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reviews Moderation */}
          <Card className="bg-[#1a1a1a] border-white/10 mt-8">
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#B8956A]" />
                Customer Reviews
                {reviews.filter(r => r.status === 'pending').length > 0 && (
                  <span className="ml-2 text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-semibold">
                    {reviews.filter(r => r.status === 'pending').length} pending
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reviews.length === 0 ? (
                <div className="text-center py-12 text-white/60">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No reviews submitted yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} data-testid={`admin-review-${review.id}`} className="bg-black/30 p-6 rounded-lg border border-white/5 hover:border-[#B8956A]/30 transition-all">
                      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-lg font-semibold text-white">{review.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${review.status === 'approved' ? 'bg-green-500/20 text-green-400' : review.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {review.status}
                            </span>
                          </div>
                          {review.role && <p className="text-white/50 text-sm">{review.role}</p>}
                          <div className="flex gap-1 mt-2">
                            {[...Array(5)].map((_, s) => (
                              <Star key={s} className={`h-4 w-4 ${s < review.rating ? 'fill-[#B8956A] text-[#B8956A]' : 'text-white/20'}`} />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => updateReviewStatus(review.id, 'approved')}
                            disabled={review.status === 'approved'}
                            data-testid={`approve-review-${review.id}`}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-4 py-2 disabled:opacity-40"
                          >
                            Approve
                          </Button>
                          <Button
                            onClick={() => updateReviewStatus(review.id, 'rejected')}
                            disabled={review.status === 'rejected'}
                            data-testid={`reject-review-${review.id}`}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs px-4 py-2 disabled:opacity-40"
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                      <div className="bg-white/5 p-4 rounded-lg">
                        <p className="text-sm text-white/80 leading-relaxed italic">"{review.comment}"</p>
                      </div>
                      <div className="mt-3 text-xs text-white/40">
                        Received: {formatDate(review.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
